/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import DayTimeline from './DayTimeline';
import UnfilledShiftsModal from './UnfilledShiftsModal';
import { projectDailySales } from './projection';
import { navigate } from './lib/navigate';

const MEAL_PERIODS = ['Breakfast', 'Lunch', 'Dinner', 'Late Night'];

// Manager landing page — a glance-and-go daily briefing.
// Pulls today's shifts, last-week KPIs (sales, labor, food, prime), alerts (unfilled
// shifts, overtime risk, price spikes, pending requests), pending action counts, and
// an 8-week trend. All read-only — no new tables. Mobile stacks single-column with
// abbreviated KPI labels via Tailwind responsive classes.

function getWeekDates(offset = 0, weekStartDay = 0, anchor = new Date()) {
  const today = anchor.getDay();
  const backToStart = (today - weekStartDay + 7) % 7;
  const start = new Date(anchor);
  start.setDate(anchor.getDate() - backToStart + offset * 7);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}
function fmtDate(d) { return d.toISOString().split('T')[0]; }
function fmtMoneyShort(n) {
  const abs = Math.abs(Number(n) || 0);
  if (abs >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtPct(n) { return n != null && isFinite(n) ? `${n.toFixed(1)}%` : '—'; }
function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let s = sh + (sm || 0) / 60;
  let e = eh + (em || 0) / 60;
  if (e < s) e += 24;
  return Math.max(0, e - s);
}
function greetingFor(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Home({ companyId, user, setCurrentPage }) {
  const [loading, setLoading] = useState(true);
  const [weekStartDay, setWeekStartDay] = useState(0);
  const [managerName, setManagerName] = useState('');
  const [todayShifts, setTodayShifts] = useState([]);
  const [unfilledToday, setUnfilledToday] = useState([]);
  // Rolling 7-day unfilled buckets, one entry per day that has unfilled shifts.
  // Each: { date, label, shifts: [{ period, role, count }] }.
  const [unfilledUpcoming, setUnfilledUpcoming] = useState([]);
  const [overtimeToday, setOvertimeToday] = useState([]);
  const [calledOutToday, setCalledOutToday] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [thisWeekHasShifts, setThisWeekHasShifts] = useState(false);
  // True when no shifts exist for any day 14+ days out — manager hasn't extended the schedule far enough.
  const [scheduleHorizonShort, setScheduleHorizonShort] = useState(false);
  // Latest scheduled day (any active shift) — drives the "Schedule posted through X" subtitle
  // and the "Work on schedule for week of Y" CTA pointing to the next un-scheduled week.
  const [maxScheduledDay, setMaxScheduledDay] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showUnfilledModal, setShowUnfilledModal] = useState(false);
  const [kpis, setKpis] = useState({
    sales: null, salesPrev: null,
    laborPct: null, laborPctPrev: null,
    foodPct: null, foodPctPrev: null,
    primePct: null, primePctPrev: null,
  });
  const [trend, setTrend] = useState([]);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unparsedInvoices, setUnparsedInvoices] = useState(0);
  const [now, setNow] = useState(new Date());

  const firstName = useMemo(() => {
    // Priority: companies.manager_name (set in onboarding) → auth metadata → email prefix.
    if (managerName) return managerName.split(' ')[0];
    const meta = user?.user_metadata?.name || user?.user_metadata?.full_name || '';
    if (meta) return meta.split(' ')[0];
    if (user?.email) return user.email.split('@')[0].replace(/[._-]/g, ' ').split(' ')[0];
    return '';
  }, [managerName, user]);

  // Once on mount, pull the company's week-start preference + manager name.
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const [settingsRes, companyRes] = await Promise.all([
        supabase.from('food_cost_settings').select('week_start_day').eq('company_id', companyId).maybeSingle(),
        supabase.from('companies').select('manager_name').eq('id', companyId).maybeSingle(),
      ]);
      if (settingsRes.data) setWeekStartDay(Number(settingsRes.data.week_start_day) || 0);
      if (companyRes.data?.manager_name) setManagerName(companyRes.data.manager_name);
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchAll();
  }, [companyId, weekStartDay]);

  // Refresh pending counts on the same 30s cadence App.js uses for sidebar badges.
  useEffect(() => {
    if (!companyId) return;
    const i = setInterval(fetchPendingCounts, 30000);
    return () => clearInterval(i);
  }, [companyId]);

  // Tick "now" every minute so the greeting + relative date stay correct without a manual reload.
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(i);
  }, []);

  async function fetchAll() {
    setLoading(true);
    await Promise.all([
      fetchToday(),
      fetchKpisAndTrend(),
      fetchPendingCounts(),
      fetchAlerts(),
    ]);
    setLoading(false);
  }

  async function fetchToday() {
    const today = fmtDate(new Date());
    // Rolling 7-day window: today through today+6.
    const rolling7 = Array.from({ length: 7 }, (_, i) => fmtDate(new Date(Date.now() + i * 86400000)));
    // Horizon check window: any shift on day +14 or later means the schedule is posted at least 2 weeks out.
    const twoWeeksOut = fmtDate(new Date(Date.now() + 14 * 86400000));
    const week = getWeekDates(0, weekStartDay).map(fmtDate);

    const [shiftsRes, empsRes, staffingRes, weekShiftsRes, horizonRes, maxDayRes, serviceRes] = await Promise.all([
      supabase.from('shifts').select('*').eq('company_id', companyId).eq('day', today).eq('is_active', true).order('start_time', { ascending: true }),
      supabase.from('employees').select('id, name, initials, color, role, max_hours_per_week, hourly_rate').eq('company_id', companyId),
      supabase.from('staffing_levels').select('*').eq('company_id', companyId),
      supabase.from('shifts').select('id, day').eq('company_id', companyId).in('day', week).eq('is_active', true).limit(1),
      supabase.from('shifts').select('id', { count: 'exact', head: true }).eq('company_id', companyId).gte('day', twoWeeksOut).eq('is_active', true),
      supabase.from('shifts').select('day').eq('company_id', companyId).eq('is_active', true).order('day', { ascending: false }).limit(1),
      // Service templates tell us which periods are actually served on each day-of-week.
      // Without this filter we'd flag "Unfilled · Breakfast" on a day the restaurant isn't even open
      // for breakfast — that's noise the manager can't act on.
      supabase.from('service_templates').select('day_of_week, meal_period, is_active').eq('company_id', companyId).eq('is_active', true),
    ]);
    setMaxScheduledDay(maxDayRes.data?.[0]?.day || null);
    // Build a {dayShortName: Set(activePeriods)} lookup for quick filtering in unfilledFor.
    const activePeriodsByDay = {};
    (serviceRes.data || []).forEach(r => {
      if (!activePeriodsByDay[r.day_of_week]) activePeriodsByDay[r.day_of_week] = new Set();
      activePeriodsByDay[r.day_of_week].add(r.meal_period);
    });
    const empMap = {};
    (empsRes.data || []).forEach(e => { empMap[String(e.id)] = e; });
    const enriched = (shiftsRes.data || []).map(s => ({ ...s, emp: empMap[String(s.employee_id)] }));
    setTodayShifts(enriched);
    setThisWeekHasShifts((weekShiftsRes.data || []).length > 0);
    setScheduleHorizonShort((horizonRes.count || 0) === 0);

    // Called-out today: shifts the user marked as no-shows in DayTimeline.
    setCalledOutToday(enriched.filter(s => s.called_out));

    // Unfilled = required minus filled per (day, period, role). Now computed across the
    // rolling 7-day window so unfilled shifts on Wed–Sat surface alongside today/tomorrow.
    const allShiftsRes = await supabase.from('shifts').select('day, shift_name, role, employee_id, is_active').eq('company_id', companyId).in('day', rolling7).eq('is_active', true);
    // staffing_levels.day_of_week is stored as 3-letter abbreviations (Mon, Tue, ...).
    // Use the matching short form here — long form ("Saturday") never matches and silently
    // returns zero unfilled, which was the bug behind "All clear" with empty shifts.
    const dayName = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
    const unfilledFor = (date) => {
      const dn = dayName(date);
      const activePeriods = activePeriodsByDay[dn] || new Set();
      // Only consider staffing requirements for periods that are actually served on this day.
      const required = (staffingRes.data || []).filter(r => r.day_of_week === dn && activePeriods.has(r.meal_period));
      const filledCounts = {};
      (allShiftsRes.data || []).filter(s => s.day === date && s.employee_id).forEach(s => {
        const k = `${s.shift_name}|${s.role}`;
        filledCounts[k] = (filledCounts[k] || 0) + 1;
      });
      const out = [];
      required.forEach(r => {
        const k = `${r.meal_period}|${r.role}`;
        const need = (Number(r.required_count) || 0) - (filledCounts[k] || 0);
        if (need > 0) out.push({ period: r.meal_period, role: r.role, count: need, date });
      });
      return out;
    };
    // Build per-day buckets. Today is broken out so the alerts row can colour it red.
    const todayBucket = unfilledFor(today);
    setUnfilledToday(todayBucket);
    const upcoming = [];
    for (let i = 1; i < rolling7.length; i++) {
      const d = rolling7[i];
      const items = unfilledFor(d);
      if (items.length === 0) continue;
      const dt = new Date(d + 'T00:00:00');
      const label = i === 1
        ? 'tomorrow'
        : dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      upcoming.push({ date: d, label, total: items.reduce((s, x) => s + x.count, 0), shifts: items });
    }
    setUnfilledUpcoming(upcoming);
  }

  async function fetchKpisAndTrend() {
    // 9 weeks back so we can show 8-week trend AND week-over-week deltas for the latest finished week.
    const NUM_WEEKS = 9;
    const weeks = [];
    for (let i = NUM_WEEKS - 1; i >= 0; i--) {
      const wd = getWeekDates(-i, weekStartDay);
      weeks.push({ start: fmtDate(wd[0]), end: fmtDate(wd[6]), dates: wd.map(fmtDate), label: wd[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
    }
    const rangeStart = weeks[0].start;
    const rangeEnd = weeks[NUM_WEEKS - 1].end;
    // Pull 6 extra weeks of history so projectDailySales has enough DOW data for partial-week fills.
    const histStartDate = new Date(rangeStart + 'T00:00:00');
    histStartDate.setDate(histStartDate.getDate() - 42);
    const histStartStr = fmtDate(histStartDate);

    const [salesRes, foodRes, shiftRes, empRes] = await Promise.all([
      supabase.from('labor_sales').select('date, total_sales, projected_sales, is_weekly_total').eq('company_id', companyId).gte('date', histStartStr).lte('date', rangeEnd),
      supabase.from('food_cost_entries').select('date, total').eq('company_id', companyId).gte('date', rangeStart).lte('date', rangeEnd),
      supabase.from('shifts').select('day, employee_id, start_time, end_time, is_active').eq('company_id', companyId).gte('day', rangeStart).lte('day', rangeEnd),
      supabase.from('employees').select('id, hourly_rate').eq('company_id', companyId),
    ]);
    const empRate = {};
    (empRes.data || []).forEach(e => { empRate[String(e.id)] = Number(e.hourly_rate) || 0; });
    const salesByDate = {};
    const salesOverrides = {};
    (salesRes.data || []).forEach(r => {
      salesByDate[r.date] = Number(r.total_sales || 0);
      if (r.projected_sales != null) salesOverrides[r.date] = Number(r.projected_sales);
    });
    const allFood = foodRes.data || [];
    const allShifts = (shiftRes.data || []).filter(s => s.is_active !== false);
    const todayStr = fmtDate(new Date());

    const rows = weeks.map(w => {
      // Build two sales totals per week:
      //   - `sales`        — projection-padded (real entered + DOW projection for
      //                      today + future). Used for display ("$3.4k last week")
      //                      and the KPI card's $-secondary line.
      //   - `actualSales`  — only days the operator ENTERED with actual > 0.
      //                      Used as the denominator for cost-as-%-of-sales math.
      // Plus `actualDays` — count of days in the week with actual > 0. Drives
      // the data-completeness gate below.
      const projMap = projectDailySales(w.dates, salesByDate, salesOverrides);
      let actualSales = 0;
      let actualDays = 0;
      const sales = w.dates.reduce((sum, ds) => {
        const actual = salesByDate[ds];
        if (actual != null && actual > 0) { actualSales += actual; actualDays++; return sum + actual; }
        if (ds < todayStr) return sum;
        return sum + (projMap[ds] || 0);
      }, 0);
      const food = allFood.filter(r => r.date >= w.start && r.date <= w.end).reduce((s, r) => s + Number(r.total || 0), 0);
      const labor = allShifts.filter(r => r.day >= w.start && r.day <= w.end).reduce((s, sh) => s + calcHours(sh.start_time, sh.end_time) * (empRate[String(sh.employee_id)] || 0), 0);
      // Data-completeness gate: a week needs a majority of days with actual
      // entered sales (≥4 of 7) before we compute %-of-sales. Avoids the
      // "operator entered Sunday, Monday-Saturday are zeros" artifact where
      // week labor covers 7 days but the denominator only covers 1 day,
      // producing 300%+ phantom spikes. See DESIGN_SYSTEM.md §Charts handle
      // gap weeks by rendering null, not zero.
      const hasCompleteData = actualDays >= 4 && actualSales > 0;
      const laborPct = hasCompleteData ? (labor / actualSales) * 100 : null;
      const foodPct = hasCompleteData ? (food / actualSales) * 100 : null;
      const primePct = (laborPct != null && foodPct != null) ? laborPct + foodPct : null;
      return { ...w, sales, actualSales, actualDays, food, labor, laborPct, foodPct, primePct };
    });
    // Trend = last 8 entries (oldest through latest).
    setTrend(rows.slice(-8));

    // KPI = "last completed week" (offset = -1) to avoid mid-week noise. weekOffset 0 is current.
    const lastWeek = rows[rows.length - 2] || null;
    const priorWeek = rows[rows.length - 3] || null;
    setKpis({
      sales: lastWeek?.sales ?? null,
      salesPrev: priorWeek?.sales ?? null,
      laborPct: lastWeek?.laborPct ?? null,
      laborPctPrev: priorWeek?.laborPct ?? null,
      laborDollars: lastWeek?.labor ?? null,
      foodPct: lastWeek?.foodPct ?? null,
      foodPctPrev: priorWeek?.foodPct ?? null,
      foodDollars: lastWeek?.food ?? null,
      primePct: lastWeek?.primePct ?? null,
      primePctPrev: priorWeek?.primePct ?? null,
      primeDollars: (lastWeek?.labor != null && lastWeek?.food != null) ? lastWeek.labor + lastWeek.food : null,
    });
  }

  async function fetchPendingCounts() {
    const queries = [
      supabase.from('requests').select('id', { count: 'exact', head: true }).eq('company_id', companyId).eq('status', 'pending'),
      supabase.from('swap_requests').select('id', { count: 'exact', head: true }).eq('company_id', companyId).eq('status', 'pending_manager'),
      supabase.from('messages').select('id', { count: 'exact', head: true }).eq('company_id', companyId).eq('read', false).not('sender_id', 'is', null),
      supabase.from('invoices').select('id', { count: 'exact', head: true }).eq('company_id', companyId).eq('status', 'pending'),
    ];
    const [reqRes, swapRes, msgRes, invRes] = await Promise.all(queries);
    setPendingRequests((reqRes.count || 0) + (swapRes.count || 0));
    setUnreadMessages(msgRes.count || 0);
    setUnparsedInvoices(invRes.count || 0);
  }

  async function fetchAlerts() {
    // Overtime TODAY only: who has shifts today AND those shifts push their weekly total over max?
    // Doesn't flag people who are at max but aren't working today — those are static, not actionable.
    const today = fmtDate(new Date());
    const week = getWeekDates(0, weekStartDay).map(fmtDate);
    const [shiftRes, empRes] = await Promise.all([
      supabase.from('shifts').select('employee_id, start_time, end_time, day').eq('company_id', companyId).in('day', week).eq('is_active', true),
      supabase.from('employees').select('id, name, max_hours_per_week').eq('company_id', companyId).not('max_hours_per_week', 'is', null),
    ]);
    const weekHours = {};
    const todayHours = {};
    (shiftRes.data || []).forEach(s => {
      const k = String(s.employee_id);
      const hrs = calcHours(s.start_time, s.end_time);
      weekHours[k] = (weekHours[k] || 0) + hrs;
      if (s.day === today) todayHours[k] = (todayHours[k] || 0) + hrs;
    });
    const overtime = [];
    (empRes.data || []).forEach(e => {
      const max = Number(e.max_hours_per_week) || 0;
      const todayH = todayHours[String(e.id)] || 0;
      const weekH = weekHours[String(e.id)] || 0;
      // Only flag if they're working today AND today's shifts contribute to going over max.
      if (max > 0 && todayH > 0 && weekH > max) {
        overtime.push({ name: e.name, scheduled: weekH, max, over: weekH - max });
      }
    });
    overtime.sort((a, b) => b.over - a.over);
    setOvertimeToday(overtime);

    // Price spikes — silently degrade if the food_items table isn't there yet.
    try {
      const { data } = await supabase
        .from('food_items')
        .select('normalized_name, price_change_pct')
        .eq('company_id', companyId)
        .gt('price_change_pct', 10)
        .order('price_change_pct', { ascending: false })
        .limit(3);
      setPriceAlerts(data || []);
    } catch {
      setPriceAlerts([]);
    }
  }

  // ── derived ──
  const today = now;
  const greeting = greetingFor(today.getHours());
  const todayLabel = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const scheduleStatus = thisWeekHasShifts ? 'Schedule posted' : 'No schedule yet — head to Schedule';

  // Derived: "Schedule posted through {end of latest scheduled week}" + "Work on week of {next start}".
  // Uses the configured weekStartDay so e.g. Sun-Sat shops align cleanly. Returns null fields when
  // there's no scheduled data yet so the subtitle just falls back to the simple status.
  const scheduleHorizon = useMemo(() => {
    if (!maxScheduledDay) return { postedThrough: null, nextWeekStart: null };
    const md = new Date(maxScheduledDay + 'T00:00:00');
    // Find the END of the week (weekStartDay + 6) that contains maxScheduledDay.
    const dow = md.getDay();
    const daysFromStart = (dow - weekStartDay + 7) % 7;
    const weekStart = new Date(md);
    weekStart.setDate(md.getDate() - daysFromStart);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    // Next week to schedule = the week immediately after.
    const nextWeekStart = new Date(weekEnd);
    nextWeekStart.setDate(weekEnd.getDate() + 1);
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
    return {
      postedThrough: weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      nextWeekStart: fmt(nextWeekStart),
    };
  }, [maxScheduledDay, weekStartDay]);

  // KPI deltas — sales is "good when up", costs are "good when down".
  const deltaSales = (kpis.sales != null && kpis.salesPrev) ? ((kpis.sales - kpis.salesPrev) / kpis.salesPrev) * 100 : null;
  const deltaLabor = (kpis.laborPct != null && kpis.laborPctPrev != null) ? kpis.laborPct - kpis.laborPctPrev : null;
  const deltaFood = (kpis.foodPct != null && kpis.foodPctPrev != null) ? kpis.foodPct - kpis.foodPctPrev : null;
  const deltaPrime = (kpis.primePct != null && kpis.primePctPrev != null) ? kpis.primePct - kpis.primePctPrev : null;

  const hasAnyAlert = unfilledToday.length > 0 || unfilledUpcoming.length > 0 || overtimeToday.length > 0 || calledOutToday.length > 0 || priceAlerts.length > 0 || pendingRequests > 0 || scheduleHorizonShort;

  // Group today's shifts by period for the new Today card. Each period row shows the
  // total scheduled and a role breakdown nested inside.
  const todayByPeriod = useMemo(() => {
    const grouped = {};
    todayShifts.forEach(s => {
      const period = s.shift_name || 'Other';
      if (!grouped[period]) grouped[period] = { period, total: 0, roles: {} };
      grouped[period].total += 1;
      const role = s.role || 'Other';
      grouped[period].roles[role] = (grouped[period].roles[role] || 0) + 1;
    });
    // Stable order (Breakfast/Lunch/Dinner/Late Night) followed by anything custom.
    const known = MEAL_PERIODS.filter(p => grouped[p]);
    const extras = Object.keys(grouped).filter(p => !MEAL_PERIODS.includes(p));
    return [...known, ...extras].map(p => grouped[p]);
  }, [todayShifts]);

  // Pending Actions navigation. Price Tracker now lives under the Inventory
  // top-level group; cross-page navigation flows through the app:navigate
  // event in src/lib/navigate.js.
  const goPriceTracker = () => navigate('inventory', 'pricetracker');
  const pendingItems = [
    { label: 'Pending requests', count: pendingRequests, onClick: () => setCurrentPage('requests') },
    { label: 'Unread messages', count: unreadMessages, onClick: () => setCurrentPage('messages') },
    { label: 'Unparsed invoices', count: unparsedInvoices, onClick: goPriceTracker },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Greeting — H1 24/600, subtitle body 14 / secondary */}
        <div className="mb-5 md:mb-6">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
            {greeting}{firstName ? `, ${firstName}` : ''}
          </h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-xs md:text-sm" style={{ color: 'var(--text-secondary)' }}>
            <span>{todayLabel}</span>
            {/* Mobile: compact status badge — state goes in a badge per "badges = state" rule. */}
            <span className="md:hidden">
              <StatusBadge tone={thisWeekHasShifts ? 'green' : 'amber'}>{scheduleStatus}</StatusBadge>
            </span>
            {/* Desktop: badge for the posted state + a purple-on-hover link for the action. */}
            <span className="hidden md:inline-flex items-center gap-2">
              {scheduleHorizon.postedThrough ? (
                <>
                  <StatusBadge tone="green">Posted through {scheduleHorizon.postedThrough}</StatusBadge>
                  {scheduleHorizon.nextWeekStart && (
                    <button
                      onClick={() => setCurrentPage('dashboard')}
                      className="font-medium text-gray-900 hover:text-[#6D28D9] hover:underline"
                    >
                      Work on schedule for week of {scheduleHorizon.nextWeekStart} →
                    </button>
                  )}
                </>
              ) : (
                <StatusBadge tone="amber">{scheduleStatus}</StatusBadge>
              )}
            </span>
          </div>
        </div>

        {/* KPIs (mobile order: after Alerts) — desktop: top row */}
        <div className="hidden md:grid grid-cols-4 gap-4 mb-6">
          <KpiCard label="Last week's sales" value={kpis.sales != null ? fmtMoneyShort(kpis.sales) : '—'} delta={deltaSales} deltaSuffix="%" goodWhenUp={true} loading={loading} />
          <KpiCard label="Labor cost" value={fmtPct(kpis.laborPct)} secondary={kpis.laborDollars != null ? fmtMoneyShort(kpis.laborDollars) : null} delta={deltaLabor} deltaSuffix="pp" goodWhenUp={false} loading={loading} />
          <KpiCard label="Food cost" value={fmtPct(kpis.foodPct)} secondary={kpis.foodDollars != null ? fmtMoneyShort(kpis.foodDollars) : null} delta={deltaFood} deltaSuffix="pp" goodWhenUp={false} loading={loading} />
          <KpiCard label="Prime cost" value={fmtPct(kpis.primePct)} secondary={kpis.primeDollars != null ? fmtMoneyShort(kpis.primeDollars) : null} delta={deltaPrime} deltaSuffix="pp" goodWhenUp={false} loading={loading} />
        </div>

        {/* Mobile: Alerts first (problems first), then KPIs 2x2, then team, then pending, then trend. */}
        <div className="md:hidden mb-4">
          <AlertsCard
            unfilledToday={unfilledToday}
            unfilledUpcoming={unfilledUpcoming}
            overtimeToday={overtimeToday}
            calledOutToday={calledOutToday}
            priceAlerts={priceAlerts}
            pendingRequests={pendingRequests}
            scheduleHorizonShort={scheduleHorizonShort}
            hasAnyAlert={hasAnyAlert}
            onClickUnfilled={() => setShowUnfilledModal(true)}
          />
        </div>
        <div className="md:hidden grid grid-cols-2 gap-3 mb-4">
          <KpiCard label="Last wk sales" value={kpis.sales != null ? fmtMoneyShort(kpis.sales) : '—'} delta={deltaSales} deltaSuffix="%" goodWhenUp={true} loading={loading} compact />
          <KpiCard label="Labor" value={fmtPct(kpis.laborPct)} secondary={kpis.laborDollars != null ? fmtMoneyShort(kpis.laborDollars) : null} delta={deltaLabor} deltaSuffix="pp" goodWhenUp={false} loading={loading} compact />
          <KpiCard label="Food" value={fmtPct(kpis.foodPct)} secondary={kpis.foodDollars != null ? fmtMoneyShort(kpis.foodDollars) : null} delta={deltaFood} deltaSuffix="pp" goodWhenUp={false} loading={loading} compact />
          <KpiCard label="Prime" value={fmtPct(kpis.primePct)} secondary={kpis.primeDollars != null ? fmtMoneyShort(kpis.primeDollars) : null} delta={deltaPrime} deltaSuffix="pp" goodWhenUp={false} loading={loading} compact />
        </div>

        {/* Two-column body — desktop only. Mobile renders these stacked below. */}
        <div className="hidden md:grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <div className="space-y-4 min-w-0">
            <TodaysTeamCard
              periods={todayByPeriod}
              total={todayShifts.length}
              unfilled={unfilledToday}
              loading={loading}
              onViewDetail={() => setShowTimeline(true)}
            />
            <TrendCard trend={trend} />
          </div>
          <div className="space-y-4">
            <AlertsCard
              unfilledToday={unfilledToday}
              unfilledUpcoming={unfilledUpcoming}
              overtimeToday={overtimeToday}
              calledOutToday={calledOutToday}
              priceAlerts={priceAlerts}
              pendingRequests={pendingRequests}
              scheduleHorizonShort={scheduleHorizonShort}
              hasAnyAlert={hasAnyAlert}
              onClickUnfilled={() => setShowUnfilledModal(true)}
            />
            <PendingCard items={pendingItems} />
          </div>
        </div>

        {/* Mobile body */}
        <div className="md:hidden space-y-4">
          <TodaysTeamCard
            periods={todayByPeriod}
            total={todayShifts.length}
            unfilled={unfilledToday}
            loading={loading}
            onViewDetail={() => setShowTimeline(true)}
            compact
          />
          <PendingCard items={pendingItems} compact />
          <TrendCard trend={trend} />
        </div>
      </div>

      {/* Timeline drawer — drawn above content on click of "View detail". Uses overflow-y-auto
         so the list scrolls vertically inside the modal; max-h-[90vh] caps it to viewport. */}
      {showTimeline && (
        <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowTimeline(false)}>
          <div className="bg-white w-full h-full md:h-auto md:max-w-5xl md:max-h-[90vh] md:rounded-lg overflow-y-auto m-0 md:m-4" onClick={(e) => e.stopPropagation()} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <DayTimeline companyId={companyId} date={fmtDate(new Date())} onClose={() => { setShowTimeline(false); fetchAll(); }} />
          </div>
        </div>
      )}

      {/* Unfilled-shifts modal — opens from the alerts summary. Lets the manager fill each slot. */}
      {showUnfilledModal && (
        <UnfilledShiftsModal
          companyId={companyId}
          slots={[
            ...unfilledToday.map(s => ({ ...s, label: 'today', isToday: true })),
            ...unfilledUpcoming.flatMap(b => b.shifts.map(s => ({ ...s, label: b.label, isToday: false }))),
          ]}
          onClose={() => { setShowUnfilledModal(false); fetchAll(); }}
          onGoToSchedule={() => { setShowUnfilledModal(false); setCurrentPage('dashboard'); }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, secondary, delta, deltaSuffix = '%', goodWhenUp, loading, compact }) {
  // Delta badges use the spec functional-color values (#D1FAE5 / #059669 for positive,
  // #FEE2E2 / #DC2626 for negative). Sales is good-when-up; cost percentages are good-when-down.
  let badgeStyle = { background: 'var(--bg-subtle)', color: 'var(--text-muted)' };
  let arrow = '';
  if (delta != null && isFinite(delta)) {
    const isUp = delta > 0;
    const isFlat = Math.abs(delta) < (deltaSuffix === 'pp' ? 0.5 : 1);
    if (!isFlat) {
      const good = goodWhenUp ? isUp : !isUp;
      badgeStyle = good
        ? { background: 'var(--bg-success-subtle)', color: 'var(--text-success)' }
        : { background: 'var(--bg-critical-subtle)', color: 'var(--text-critical)' };
      arrow = isUp ? '↑' : '↓';
    }
  }
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-5">
      <div className={`${compact ? 'text-[11px]' : 'text-xs'} font-medium mb-1.5 uppercase tracking-wide`} style={{ color: 'var(--text-secondary)' }}>{label}</div>
      {loading ? (
        <div className={`${compact ? 'h-7' : 'h-9'} w-28 bg-gray-100 rounded animate-pulse`} />
      ) : (
        <div className={`${compact ? 'text-2xl' : 'text-[32px] leading-none'} font-bold tracking-tight text-gray-900`}>{value}</div>
      )}
      {/* Secondary line — labor / food / prime dollar amount. 14px body, secondary color. */}
      {secondary && !loading && (
        <div className={`${compact ? 'text-[11px]' : 'text-sm'} mt-1`} style={{ color: 'var(--text-secondary)' }}>{secondary}</div>
      )}
      <div className="mt-2 h-5 flex items-center">
        {delta != null && isFinite(delta) ? (
          <span className="text-[11px] font-medium rounded-md px-2 py-0.5" style={badgeStyle}>
            {arrow} {Math.abs(delta).toFixed(1)}{deltaSuffix === 'pp' ? 'pp' : '%'}
          </span>
        ) : (
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>vs prior week</span>
        )}
      </div>
    </div>
  );
}

// Small inline badge for state signaling (per "badges = state" principle).
function StatusBadge({ tone = 'neutral', children }) {
  const tones = {
    green:   { background: 'var(--bg-success-subtle)',  color: 'var(--text-success)' },
    amber:   { background: 'var(--bg-warning-subtle)',  color: 'var(--text-warning)' },
    red:     { background: 'var(--bg-critical-subtle)', color: 'var(--text-critical)' },
    blue:    { background: 'var(--bg-info-subtle)',     color: 'var(--text-info)' },
    neutral: { background: 'var(--bg-subtle)',          color: 'var(--text-secondary)' },
  };
  return (
    <span className="inline-flex items-center text-[11px] font-medium rounded-md px-2 py-0.5" style={tones[tone] || tones.neutral}>
      {children}
    </span>
  );
}

function TodaysTeamCard({ periods, total, unfilled, loading, onViewDetail, compact }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full" style={{ background: 'var(--text-success)' }} />
          <h2 className="text-sm font-semibold text-gray-900 truncate">Today — {total} scheduled</h2>
        </div>
        {(total > 0 || unfilled.length > 0) && (
          <button
            onClick={onViewDetail}
            className="text-xs font-medium text-gray-900 hover:underline whitespace-nowrap"
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent-primary)')}
            onMouseLeave={e => (e.currentTarget.style.color = '')}
          >
            View detail →
          </button>
        )}
      </div>
      <div className={`${compact ? 'p-3 space-y-2' : 'p-5 space-y-2'}`}>
        {loading ? (
          [0, 1, 2].map(i => <div key={i} className="h-12 rounded-lg bg-gray-100 animate-pulse" />)
        ) : periods.length === 0 && unfilled.length === 0 ? (
          <div className="py-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No shifts scheduled today</div>
        ) : (
          <>
            {periods.map(p => <PeriodRow key={p.period} period={p} compact={compact} />)}
            {unfilled.map((u, i) => <UnfilledRow key={`u-${i}`} unfilled={u} compact={compact} />)}
          </>
        )}
      </div>
    </div>
  );
}

// Meal-period section rule: tonal-gray background + 3px colored left bar, subtle label.
// Backgrounds come from CSS vars (--meal-bg-*) so the light tonal gray swaps to a
// whisper-tint of the period's accent hue in .dark mode. Left bars stay the same —
// they read in both themes.
const PERIOD_TREATMENT = {
  'Breakfast':  { bg: 'var(--meal-bg-breakfast)', bar: 'var(--text-warning)' },
  'Lunch':      { bg: 'var(--meal-bg-lunch)',     bar: 'var(--text-info)' },
  'Dinner':     { bg: 'var(--meal-bg-dinner)',    bar: 'var(--accent-primary)' },
  'Late Night': { bg: 'var(--meal-bg-dinner)',    bar: 'var(--accent-primary)' },
};

function PeriodRow({ period, compact }) {
  const roles = Object.entries(period.roles).sort((a, b) => b[1] - a[1]);
  const treat = PERIOD_TREATMENT[period.period] || { bg: 'var(--bg-subtle)', bar: 'var(--text-muted)' };
  return (
    <div
      className={`${compact ? 'px-3 py-2' : 'px-3 py-2.5'} rounded-lg`}
      style={{ background: treat.bg, borderLeft: `3px solid ${treat.bar}` }}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>{period.period}</div>
        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{period.total} scheduled</div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {roles.map(([role, count]) => (
          <span key={role} className="inline-flex items-center gap-1 text-[11px] font-medium bg-white border border-gray-200 rounded-md px-2 py-0.5" style={{ color: 'var(--text-secondary)' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: ROLE_DOT[role] || '#9CA3AF' }} />
            {count} {role}{count === 1 ? '' : 's'}
          </span>
        ))}
      </div>
    </div>
  );
}

// Role dots are data colors (per DESIGN_SYSTEM.md Brand/UI vs Data colors). Cook stays orange, etc.
const ROLE_DOT = {
  'Cook': '#EA580C', 'Prep Cook': '#DC2626', 'Dishwasher': '#64748B',
  'Server': '#2563EB', 'Bartender': '#D97706', 'Host': '#9333EA',
  'Busser': '#0D9488', 'Manager': '#4B5563',
};

function UnfilledRow({ unfilled, compact }) {
  // Alert-style row per the functional red spec: 3px left border, light red bg, dark red text.
  return (
    <div
      className={`${compact ? 'px-3 py-1.5' : 'px-3 py-2'} rounded-lg flex items-center gap-2`}
      style={{ background: 'var(--bg-critical-subtle)', borderLeft: '3px solid var(--text-critical)', color: 'var(--text-critical)' }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
        <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      </svg>
      <div className="min-w-0 flex-1">
        <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium`}>Unfilled · {unfilled.role}</span>
        <span className={`${compact ? 'text-[11px]' : 'text-xs'} ml-2 opacity-80`}>{unfilled.period} · need {unfilled.count}</span>
      </div>
    </div>
  );
}

function AlertsCard({ unfilledToday, unfilledUpcoming, overtimeToday, calledOutToday, priceAlerts, pendingRequests, scheduleHorizonShort, hasAnyAlert, onClickUnfilled }) {
  // Total unfilled across the rolling 7-day window. Days hit lets us write "across N days"
  // when the user has unfilled spread out, vs just "today" / "tomorrow" when concentrated.
  const todayTotal = unfilledToday.length;
  const totalUnfilled = todayTotal + unfilledUpcoming.reduce((s, b) => s + b.total, 0);
  const daysHit = (todayTotal > 0 ? 1 : 0) + unfilledUpcoming.length;
  // Single-day labels stay specific; multi-day rolls up so the alerts card doesn't sprawl.
  let unfilledLabel = null;
  if (totalUnfilled > 0) {
    if (daysHit === 1 && todayTotal > 0) unfilledLabel = `${totalUnfilled} unfilled shift${totalUnfilled === 1 ? '' : 's'} today`;
    else if (daysHit === 1 && unfilledUpcoming.length === 1) unfilledLabel = `${unfilledUpcoming[0].total} unfilled shift${unfilledUpcoming[0].total === 1 ? '' : 's'} ${unfilledUpcoming[0].label}`;
    else unfilledLabel = `${totalUnfilled} unfilled shift${totalUnfilled === 1 ? '' : 's'} across ${daysHit} days`;
  }
  const unfilledStyle = todayTotal > 0
    ? { background: 'var(--bg-critical-subtle)', borderLeft: '3px solid var(--text-critical)', color: 'var(--text-critical)' }
    : { background: 'var(--bg-warning-subtle)',  borderLeft: '3px solid var(--text-warning)',  color: 'var(--text-warning)' };
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-warning)" strokeWidth="2"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
        <h2 className="text-sm font-semibold text-gray-900">Alerts</h2>
      </div>
      <div className="p-3 space-y-2">
        {!hasAnyAlert && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ background: 'var(--bg-success-subtle)', borderLeft: '3px solid var(--text-success)', color: 'var(--text-success)' }}>
            <span className="w-2 h-2 rounded-full" style={{ background: 'var(--text-success)' }} />
            <span className="text-xs">All clear — nothing needs attention</span>
          </div>
        )}
        {/* Day-of (most urgent first) */}
        {calledOutToday.map((s, i) => (
          <Alert key={`co-${i}`} kind="danger">
            <strong>{s.emp?.name || `Employee #${s.employee_id}`}</strong> called out — {s.shift_name || 'shift'} ({s.role || ''})
          </Alert>
        ))}
        {/* Single rolled-up unfilled alert — clickable, opens the modal that lets the manager
           pick someone to fill each slot. Today-only stays red since it's most urgent. */}
        {unfilledLabel && (
          <button
            onClick={onClickUnfilled}
            className="w-full text-left px-3 py-2 rounded-md text-xs hover:brightness-95 transition-all flex items-center justify-between gap-2"
            style={unfilledStyle}
          >
            <span><strong>{unfilledLabel.split(' ')[0]}</strong> {unfilledLabel.split(' ').slice(1).join(' ')}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        )}
        {overtimeToday.slice(0, 3).map((r, i) => (
          <Alert key={`ot-${i}`} kind="warning">
            <strong>{r.name}</strong> {r.scheduled.toFixed(1)}h this week ({r.over.toFixed(1)}h over {r.max}h cap) — has shifts today
          </Alert>
        ))}
        {/* Schedule horizon — surfaces if no shifts exist 14+ days out so the manager knows to extend. */}
        {scheduleHorizonShort && (
          <Alert kind="warning">
            Schedule isn't posted 2 weeks out — extend on the Schedule tab
          </Alert>
        )}
        {pendingRequests > 0 && (
          <Alert kind="info">
            <strong>{pendingRequests}</strong> pending request{pendingRequests === 1 ? '' : 's'} awaiting approval
          </Alert>
        )}
        {priceAlerts.slice(0, 2).map((p, i) => (
          <Alert key={`p-${i}`} kind="info">
            <strong>{p.normalized_name}</strong> up {Number(p.price_change_pct).toFixed(1)}% vs last invoice
          </Alert>
        ))}
      </div>
    </div>
  );
}

function Alert({ kind, children }) {
  const palette = {
    danger:  { background: 'var(--bg-critical-subtle)', borderLeft: '3px solid var(--text-critical)', color: 'var(--text-critical)' },
    warning: { background: 'var(--bg-warning-subtle)',  borderLeft: '3px solid var(--text-warning)',  color: 'var(--text-warning)' },
    info:    { background: 'var(--bg-info-subtle)',     borderLeft: '3px solid var(--text-info)',     color: 'var(--text-info)' },
  }[kind] || { background: 'var(--bg-subtle)', borderLeft: '3px solid var(--border-strong)', color: 'var(--text-secondary)' };
  return (
    <div className="px-3 py-2 rounded-md text-xs" style={palette}>{children}</div>
  );
}

function PendingCard({ items, compact }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">Pending</h2>
      </div>
      <div className={`${compact ? 'p-2 space-y-1' : 'p-3 space-y-2'}`}>
        {items.map(item => {
          const badge = item.count > 0
            ? { background: 'var(--bg-critical-subtle)', color: 'var(--text-critical)' }
            : { background: 'var(--bg-subtle)', color: 'var(--text-muted)' };
          return (
            <button
              key={item.label}
              onClick={item.count > 0 ? item.onClick : undefined}
              disabled={item.count === 0}
              className={`w-full flex items-center justify-between gap-2 ${compact ? 'px-3 py-2' : 'px-3 py-2.5'} rounded-lg ${item.count > 0 ? 'bg-gray-50 hover:bg-gray-100 cursor-pointer' : 'bg-gray-50 cursor-default'} text-left transition-colors`}
            >
              <span className="text-xs md:text-sm" style={{ color: item.count > 0 ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{item.label}</span>
              <span className="flex items-center gap-2">
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-md" style={badge}>{item.count}</span>
                {item.count > 0 && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)' }}><path d="M9 18l6-6-6-6"/></svg>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TrendCard({ trend }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-gray-900">8-week trend</h2>
        <div className="flex items-center gap-3 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          {/* Chart series colors are data colors — see DESIGN_SYSTEM.md Brand/UI vs Data colors */}
          <LegendDot color="#3B82F6" label="Labor" />
          <LegendDot color="#F97316" label="Food" />
          <LegendDot color="#111827" label="Prime" dashed />
        </div>
      </div>
      {trend.filter(w => w.primePct != null).length < 2 ? (
        <div className="py-8 text-center text-xs" style={{ color: 'var(--text-muted)' }}>Enter sales + costs for a couple of weeks to see the trend.</div>
      ) : (
        <TrendChart rows={trend} />
      )}
    </div>
  );
}

function LegendDot({ color, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {dashed ? (
        <svg width="14" height="2" viewBox="0 0 14 2"><line x1="0" y1="1" x2="14" y2="1" stroke={color} strokeWidth="2" strokeDasharray="3 2" /></svg>
      ) : (
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      )}
      {label}
    </span>
  );
}

function TrendChart({ rows }) {
  const W = 640, H = 160;
  const padL = 28, padR = 8, padT = 6, padB = 22;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const allPcts = rows.flatMap(r => [r.foodPct, r.laborPct, r.primePct]).filter(v => v != null);
  const maxY = Math.max(80, Math.ceil(Math.max(...allPcts, 70) / 10) * 10);
  const xAt = (i) => padL + (rows.length <= 1 ? plotW / 2 : (i / (rows.length - 1)) * plotW);
  const yAt = (v) => padT + plotH - (v / maxY) * plotH;
  // Use M for the first NON-NULL point, L for subsequent ones. Keying off i===0 broke when
  // early weeks lacked sales (path tried to start with L → SVG silently dropped the line).
  const pathFor = (key) => {
    let started = false;
    return rows.map((r, i) => {
      if (r[key] == null) return '';
      const cmd = started ? 'L' : 'M';
      started = true;
      return `${cmd}${xAt(i).toFixed(1)},${yAt(r[key]).toFixed(1)}`;
    }).filter(Boolean).join(' ');
  };
  const yTicks = [];
  for (let v = 0; v <= maxY; v += 20) yTicks.push(v);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="160" preserveAspectRatio="none" className="bg-gray-50 rounded-md">
      {yTicks.map(v => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yAt(v)} y2={yAt(v)} stroke="var(--border)" strokeWidth="1" />
          <text x={padL - 4} y={yAt(v) + 3} fontSize="9" fill="var(--text-muted)" textAnchor="end">{v}%</text>
        </g>
      ))}
      {/* Gap markers — subtle dashed verticals at weeks with null pcts.
          See DESIGN_SYSTEM.md §Charts handle gap weeks by rendering null. */}
      {rows.map((r, i) => (
        r.primePct == null ? (
          <line
            key={`gap-${i}`}
            x1={xAt(i)} x2={xAt(i)} y1={padT} y2={padT + plotH}
            stroke="var(--border)" strokeWidth="1" strokeDasharray="2 3" opacity="0.3"
          >
            <title>No sales data entered for this week.</title>
          </line>
        ) : null
      ))}
      <path d={pathFor('laborPct')} fill="none" stroke="#3B82F6" strokeWidth="2" />
      <path d={pathFor('foodPct')} fill="none" stroke="#F97316" strokeWidth="2" />
      <path d={pathFor('primePct')} fill="none" stroke="#111827" strokeWidth="2" strokeDasharray="4 3" />
      {rows.map((r, i) => (
        <g key={i}>
          {r.laborPct != null && <circle cx={xAt(i)} cy={yAt(r.laborPct)} r="2" fill="#3B82F6" />}
          {r.foodPct != null && <circle cx={xAt(i)} cy={yAt(r.foodPct)} r="2" fill="#F97316" />}
          {r.primePct != null && <circle cx={xAt(i)} cy={yAt(r.primePct)} r="2.5" fill="#111827" />}
          {(i === 0 || i === rows.length - 1 || i % 2 === 0) && (
            <text x={xAt(i)} y={H - 6} fontSize="9" fill="var(--text-muted)" textAnchor="middle">{r.label}</text>
          )}
        </g>
      ))}
    </svg>
  );
}
